import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { IBreadcrumbItem } from '@/widgets/CustomBreadcrumbs';
import {
  IEnrollmentLessonResource,
  IModuleLessonsResource,
  useGetEnrollmentLessonQuery,
  useGetModuleLessonsQuery,
} from '@/entities/Enrollment';
import { getErrorMessage } from '@/shared/lib/utils/getErrorMessage';
import { routerPath } from '@/shared/config/router';
import type { IStudentContentStep } from '@/shared/types/lesson';

type LessonDetailsData = {
  lessonData: IEnrollmentLessonResource;
  moduleData: IModuleLessonsResource;
};

type PayloadType = {
  data: LessonDetailsData | undefined;
  isLoading: boolean;
  /** Сырая ошибка запроса: по ней отличается отказ из-за долга. */
  error: unknown;
  errorMessage: string;
  isParamsValid: boolean;
  breadcrumbs: IBreadcrumbItem[];
  steps: IStudentContentStep[];
  refetchLesson: () => void;
  params: {
    enrollmentId?: string;
    intakeTermId?: string;
    intakeModuleId?: string;
    groupNumber?: string;
    groupLessonId?: string;
    groupExamResubmissionId?: string;
  };
};

const EMPTY_STEPS: IStudentContentStep[] = [];

export const useEnrollmentLessonDetails = (): PayloadType => {
  const {
    enrollmentId,
    intakeTermId,
    intakeModuleId,
    groupNumber,
    groupLessonId,
    groupExamResubmissionId,
  } = useParams();

  const hasLessonParams = Boolean(
    enrollmentId && intakeTermId && (groupLessonId || groupExamResubmissionId),
  );
  const hasModuleParams = Boolean(intakeModuleId && groupNumber);

  const lessonArg = hasLessonParams
    ? {
        enrollmentId: enrollmentId!,
        intakeTermId: intakeTermId!,
        groupLessonId: groupLessonId,
        groupExamResubmissionId: groupExamResubmissionId,
      }
    : skipToken;

  const moduleArg = hasModuleParams
    ? { intakeModuleId: intakeModuleId!, groupNumber: groupNumber! }
    : skipToken;

  const lessonQuery = useGetEnrollmentLessonQuery(lessonArg);
  const moduleQuery = useGetModuleLessonsQuery(moduleArg);

  const isLoading = lessonQuery.isLoading || moduleQuery.isLoading;
  const lessonError = lessonQuery.currentData ? undefined : lessonQuery.error;
  const error = lessonError ?? moduleQuery.error ?? undefined;
  const errorMessage = error ? (getErrorMessage(error) ?? 'Something went wrong') : '';
  const isParamsValid = hasLessonParams && hasModuleParams;

  const data = useMemo(() => {
    if (lessonQuery.data && moduleQuery.data) {
      return {
        lessonData: lessonQuery.data,
        moduleData: moduleQuery.data,
      };
    }
    return undefined;
  }, [lessonQuery.data, moduleQuery.data]);

  const lesson = lessonQuery.data?.lesson;
  const steps =
    lesson?.contentMode === 'steps' && lesson.steps?.length ? lesson.steps : EMPTY_STEPS;

  const breadcrumbs = useMemo<IBreadcrumbItem[]>(() => {
    const items: IBreadcrumbItem[] = [{ label: 'Education', href: routerPath.enrollments }];

    const courseTitle = data?.moduleData?.course?.title;
    const termNumber = data?.moduleData?.term?.number;

    if (courseTitle) {
      items.push({
        label: courseTitle,
        href: enrollmentId ? `/enrollments/${enrollmentId}` : undefined,
      });
    } else if (enrollmentId) {
      items.push({
        label: `Enrollment ${enrollmentId}`,
        href: `/enrollments/${enrollmentId}`,
      });
    }

    if (typeof termNumber === 'number') {
      items.push({
        label: `Term ${termNumber}`,
        href:
          enrollmentId && intakeTermId
            ? `/enrollments/${enrollmentId}/terms/${intakeTermId}`
            : undefined,
      });
    } else if (intakeTermId) {
      items.push({
        label: `Term ${intakeTermId}`,
        href: enrollmentId ? `/enrollments/${enrollmentId}/terms/${intakeTermId}` : undefined,
      });
    }

    if (groupLessonId || groupExamResubmissionId) {
      const lessonOrder = data?.lessonData?.lesson?.order;
      const lessonTitle = data?.lessonData?.lesson?.title;
      if (typeof lessonOrder === 'number' && lessonTitle) {
        items.push({
          label: `Lesson ${lessonOrder}. ${lessonTitle}`,
        });
      }
    }

    return items;
  }, [data, enrollmentId, intakeTermId, groupLessonId, groupExamResubmissionId]);

  return {
    data,
    isLoading,
    error,
    errorMessage,
    isParamsValid,
    breadcrumbs,
    steps,
    refetchLesson: lessonQuery.refetch,
    params: {
      enrollmentId,
      intakeTermId,
      intakeModuleId,
      groupNumber,
      groupLessonId,
      groupExamResubmissionId,
    },
  };
};
